import * as React from "react";

import Box from "@mui/material/Box";
import { Theme } from "@mui/material/styles";

import { ButtonCopy } from "../ButtonCopy/ButtonCopy";

import { TextFieldWithCopyWrapper } from "./TextFieldWithCopyWrapper";
import { TextFieldWithCopyProps } from "./types";
import { useTextFieldWithCopy } from "./useTextFieldWithCopy";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTextField } from "@/dashboard-design-system/TextField";

const getInputWrapperSx = (multiline?: boolean) => (theme: Theme) => ({
  "& .ZeffyInput-root": {
    backgroundColor: theme.palette.surface.neutral.white,
    ...(multiline ? { alignItems: "flex-end" } : {}),
  },
});

const linkSpacingSx = (theme: Theme) => ({
  marginLeft: theme.spacing(1),
});

export const TextFieldWithCopy = React.forwardRef<HTMLInputElement, TextFieldWithCopyProps>((props, ref) => {
  const {
    button = false,
    buttonCopyProps,
    copyAmplitudeEvent,
    copyAmplitudePayload,
    copyButtonBelow = false,
    hideCopyButton = false,
    multiline,
    onCopy,
    value,
    ...textFieldProps
  } = props;

  const { t } = useTranslate();
  const { handleLinkCopy, selectFullLink } = useTextFieldWithCopy({
    onCopy,
    copyAmplitudeEvent,
    copyAmplitudePayload,
    value,
  });

  return (
    <TextFieldWithCopyWrapper
      button={button}
      copyButtonBelow={copyButtonBelow}
      hideCopyButton={hideCopyButton}
      value={value}
      slots={{
        textFieldComponent: (
          <Box sx={getInputWrapperSx(multiline)}>
            <DashboardTextField
              {...textFieldProps}
              ref={ref}
              multiline={multiline}
              value={value}
              data-test={textFieldProps["data-test"] || "text-field-with-copy-icon"}
              endAdornment={
                !button && !copyButtonBelow && !hideCopyButton ? (
                  <DashboardLink vibe="brand" onClick={handleLinkCopy} sx={linkSpacingSx}>
                    {t("common", "copy")}
                  </DashboardLink>
                ) : undefined
              }
              onFocus={selectFullLink}
              size={textFieldProps.size || "small"}
            />
          </Box>
        ),
        copyButtonComponent: (
          <ButtonCopy
            variant={buttonCopyProps?.variant || "filled"}
            vibe={buttonCopyProps?.vibe || "alternate"}
            size={buttonCopyProps?.size || "small"}
            toCopy={value}
            onClick={onCopy}
            amplitudeEvent={copyAmplitudeEvent}
            amplitudePayload={copyAmplitudePayload}
            copyText={buttonCopyProps?.copyText}
          />
        ),
      }}
    />
  );
});

TextFieldWithCopy.displayName = "TextFieldWithCopy";
